import React, {useState, useEffect, useMemo} from "react";
import {observer} from "mobx-react-lite";
import {useTranslation} from "react-i18next";
import {message, Alert} from "antd";
import {CheckCircleFilled} from "@ant-design/icons";
import moment from "moment";
import {useStore} from "hooks/useStore";
import ShiftSelection from "./ShiftSelection";
import DateSelection from "./DateSelection";
import CourierCountInput from "./CourierCountInput";
import RequestDetailModal from "./RequestDetailModal";
import SupportRequestConfirmationModal from "./SupportRequestConfirmationModal";
import {
    SupportRequestContainer,
    StepsContainer,
    StepItem,
    StepNumber,
    StepDescription,
    ContentContainer,
    ActionButtonContainer,
    SubmitButton
} from "../style/CustomerRequestsPage.styles";

const SupportRequestTab = ({
    tableData,
    tableLoading,
    refetchTable,
    onPaginationChange,
    filters,
    handleUpdateFilters
}) => {
    const {t} = useTranslation();
    const stores = useStore();
    const [currentStep, setCurrentStep] = useState(1);
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [courierCount, setCourierCount] = useState(null);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [confirmationModalVisible, setConfirmationModalVisible] = useState(false);
    const [confirmationLoading, setConfirmationLoading] = useState(false);

    useEffect(() => {
        if (startTime && endTime && currentStep === 1) {
            setCurrentStep(2);
        }
    }, [startTime, endTime, currentStep]);

    useEffect(() => {
        if (startDate && endDate && currentStep === 2) {
            setCurrentStep(3);
        }
    }, [startDate, endDate, currentStep]);

    const handleSubmit = () => {
        if (!startTime || !endTime) {
            message.warning(t("screens.customerRequests.pleaseSelectShiftHours"));
            return;
        }

        if (!startDate || !endDate) {
            message.warning(t("screens.customerRequests.pleaseSelectDateRange"));
            return;
        }
        if (!courierCount || courierCount < 1) {
            message.warning(t("screens.customerRequests.pleaseEnterValidCourierCount"));
            return;
        }

        if (totalHours < 10) {
            message.warning(t("screens.customerRequests.supportShiftHoursMustBeAtLeast10"));
            return;
        }

        setConfirmationModalVisible(true);
    };

    const handleConfirmSubmit = async () => {
        setConfirmationLoading(true);

        const params = {
            type: "support",
            subType: "increment",
            startDate: moment(startDate).format("YYYY-MM-DD"),
            endDate: moment(endDate).format("YYYY-MM-DD"),
            startHour: startTime,
            endHour: endTime,
            courierCount: courierCount,
            subCompanyId: stores?.meStore?.selectedSubCompanyId || "all"
        };

        const result = await stores.shiftRequestStore.createShiftRequest(params);

        setConfirmationLoading(false);
        setConfirmationModalVisible(false);

        if (result) {
            setStartTime(null);
            setEndTime(null);
            setStartDate(null);
            setEndDate(null);
            setCourierCount(null);
            setCurrentStep(1);
            refetchTable();
        }
    };

    const handleCancelConfirmation = () => {
        setConfirmationModalVisible(false);
    };

    const getTotalHours = () => {
        if (!startTime || !endTime) return 0;

        const start = parseInt(startTime.split(":")[0]);
        const end = parseInt(endTime.split(":")[0]);

        if (end > start) {
            return end - start;
        } else if (end < start) {
            return 24 - start + end;
        }
        return 0;
    };

    const getTotalDays = () => {
        if (!startDate || !endDate) return 0;
        const start = moment(startDate).startOf("day");
        const end = moment(endDate).startOf("day");
        return end.diff(start, "days") + 1;
    };

    const getTotalCourierCount = () => {
        return courierCount;
    };

    const isStepCompleted = step => {
        switch (step) {
            case 1:
                return startTime && endTime;
            case 2:
                return startDate && endDate;
            case 3:
                return courierCount !== null && courierCount > 0;
            default:
                return false;
        }
    };

    const handleCloseDetailModal = () => {
        setDetailModalVisible(false);
        setSelectedRequest(null);
    };

    const totalHours = getTotalHours();
    const totalDays = getTotalDays();

    return (
        <SupportRequestContainer>
            <Alert
                type="warning"
                showIcon
                message={t("screens.customerRequests.onlyOneRequestPerDay")}
                style={{marginBottom: "16px"}}
            />
            <StepsContainer>
                <StepItem>
                    <StepNumber $completed={isStepCompleted(1)} $active={currentStep >= 1}>
                        {isStepCompleted(1) ? <CheckCircleFilled /> : 1}
                    </StepNumber>
                    <StepDescription>
                        {t("screens.customerRequests.shiftSelection")}
                    </StepDescription>
                </StepItem>
                <StepItem>
                    <StepNumber $completed={isStepCompleted(2)} $active={currentStep >= 2}>
                        {isStepCompleted(2) ? <CheckCircleFilled /> : 2}
                    </StepNumber>
                    <StepDescription>{t("screens.customerRequests.dateSelection")}</StepDescription>
                </StepItem>
                <StepItem>
                    <StepNumber $completed={isStepCompleted(3)} $active={currentStep >= 3}>
                        {isStepCompleted(3) ? <CheckCircleFilled /> : 3}
                    </StepNumber>
                    <StepDescription>{t("screens.customerRequests.courierCount")}</StepDescription>
                </StepItem>
            </StepsContainer>

            <ContentContainer>
                <ShiftSelection
                    startTime={startTime}
                    setStartTime={setStartTime}
                    endTime={endTime}
                    setEndTime={setEndTime}
                    totalHours={totalHours}
                    isActive={true}
                    isSupportRequest={true}
                />

                <DateSelection
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    totalDays={totalDays}
                    isActive={isStepCompleted(1)}
                />

                <CourierCountInput
                    courierCount={courierCount}
                    setCourierCount={setCourierCount}
                    isActive={isStepCompleted(1) && isStepCompleted(2)}
                    totalCourierCount={getTotalCourierCount()}
                    isSupportRequest={true}
                />
            </ContentContainer>

            <ActionButtonContainer>
                <SubmitButton
                    onClick={handleSubmit}
                    disabled={!isStepCompleted(1) || !isStepCompleted(2) || !isStepCompleted(3)}
                >
                    {t("screens.customerRequests.submitRequest")}
                </SubmitButton>
            </ActionButtonContainer>

            <RequestDetailModal
                visible={detailModalVisible}
                onClose={handleCloseDetailModal}
                requestData={selectedRequest}
                refetchTable={refetchTable}
            />

            <SupportRequestConfirmationModal
                visible={confirmationModalVisible}
                onCancel={handleCancelConfirmation}
                onConfirm={handleConfirmSubmit}
                requestData={{
                    startDate,
                    endDate,
                    courierCount,
                    totalDays,
                    totalHours
                }}
                loading={confirmationLoading}
            />
        </SupportRequestContainer>
    );
};

export default observer(SupportRequestTab);
